import { Controller } from "@hotwired/stimulus"

const FORM_SELECTOR = '#booking-form, [data-controller*="booking-form"], [data-controller*="public-wizard"]'

export default class extends Controller {
  static values = {
    loaded: { type: Boolean, default: false }
  }

  connect() {
    this.siteKey = document.querySelector('meta[name="recaptcha-site-key"]')?.content
    if (!this.siteKey) return

    this.checkAndLoadForForms()
    this.setupTurboListeners()
  }

  disconnect() {
    this.removeTurboListeners()
    this.removeInteractionListeners()
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout)
    }
  }

  checkAndLoadForForms() {
    if (this.loadedValue) return

    const hasBookingForm = document.querySelector(FORM_SELECTOR)
    if (hasBookingForm) {
      this.setupLazyLoad()
    }
  }

  setupLazyLoad() {
    this.removeInteractionListeners()

    // Load on first interaction (scroll, click, touch, mousemove)
    this.interactionHandler = () => this.loadRecaptchaScript()

    const events = ['scroll', 'click', 'touchstart', 'mousemove', 'keydown']
    events.forEach(event => {
      window.addEventListener(event, this.interactionHandler, { once: true, passive: true })
    })

    // Also load if user focuses on any form input
    const formInputs = document.querySelectorAll(`${FORM_SELECTOR} input, ${FORM_SELECTOR} select, ${FORM_SELECTOR} textarea`)
    formInputs.forEach(input => {
      input.addEventListener('focus', this.interactionHandler, { once: true })
    })

    // Fallback: load after 4 seconds if no interaction
    this.loadTimeout = setTimeout(() => this.loadRecaptchaScript(), 4000)
  }

  removeInteractionListeners() {
    if (this.interactionHandler) {
      const events = ['scroll', 'click', 'touchstart', 'mousemove', 'keydown']
      events.forEach(event => {
        window.removeEventListener(event, this.interactionHandler)
      })
    }
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
    }
  }

  loadRecaptchaScript() {
    if (this.loadedValue || !this.siteKey) return
    this.loadedValue = true

    this.removeInteractionListeners()

    // Check if script already exists
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      this.initializeRecaptcha()
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`
    script.async = true
    script.defer = true
    script.onload = () => this.initializeRecaptcha()
    document.head.appendChild(script)
  }

  setupTurboListeners() {
    this.turboBeforeVisitHandler = () => {
      this.badgeRendered = false
    }
    this.turboLoadHandler = () => {
      // After Turbo navigation, check if the new page has a booking form
      // and load reCAPTCHA if it hasn't been loaded yet
      this.checkAndLoadForForms()

      if (this.loadedValue) {
        this.initializeRecaptcha()
      }
    }

    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    document.addEventListener('turbo:load', this.turboLoadHandler)
  }

  removeTurboListeners() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
    if (this.turboLoadHandler) {
      document.removeEventListener('turbo:load', this.turboLoadHandler)
    }
  }

  initializeRecaptcha() {
    if (!this.siteKey) return

    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout)
    }

    if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
      grecaptcha.ready(() => {
        this.renderBadge()
      })
      return
    }

    let attempts = 0
    const maxAttempts = 20
    const checkInterval = 250

    const pollForRecaptcha = () => {
      attempts++
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
          this.renderBadge()
        })
      } else if (attempts < maxAttempts) {
        this.pollTimeout = setTimeout(pollForRecaptcha, checkInterval)
      }
    }

    this.pollTimeout = setTimeout(pollForRecaptcha, checkInterval)
  }

  renderBadge() {
    if (this.badgeRendered || !this.siteKey) return

    grecaptcha.execute(this.siteKey, { action: 'page_view' }).then(() => {
      this.badgeRendered = true
    }).catch((error) => {
      console.warn('reCAPTCHA badge render failed:', error)
    })
  }
}
